import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { trackPageView } from "./analytics.ts";
import { ConsentBanner } from "./components/consent.tsx";
import { Footer, Page, SiteHeader } from "./components/ui.tsx";
import { useConsent } from "./consent.ts";
import { t } from "./i18n.ts";
import { imprint, privacy, terms } from "./legal.ts";
import { Home } from "./pages/Home.tsx";
import { Legal } from "./pages/Legal.tsx";
import { RoomPage } from "./pages/RoomPage.tsx";
import { Rules } from "./pages/Rules.tsx";
import { CreateRoom, JoinRoom } from "./pages/Setup.tsx";
import { linkHandler, usePath } from "./router.ts";

// Das Admin-Backend wird nur geladen, wenn es gebraucht wird.
const Admin = lazy(() => import("./admin/Admin.tsx"));

function NotFound() {
  return (
    <>
      <SiteHeader />
      <Page narrow>
        <h1>404</h1>
        <a href="/" onClick={linkHandler("/")}>
          {t.toHome}
        </a>
      </Page>
      <Footer />
    </>
  );
}

function Content({ path }: { path: string }): ReactNode {
  if (path === "/") return <Home />;
  if (path === "/regeln") return <Rules />;
  if (path === "/neu") return <CreateRoom />;
  if (path === "/beitreten") return <JoinRoom initialCode={new URLSearchParams(location.search).get("code") ?? ""} />;
  if (path === "/impressum") return <Legal page={imprint} />;
  if (path === "/nutzungsbedingungen") return <Legal page={terms} />;
  if (path === "/datenschutz") return <Legal page={privacy} />;
  const room = /^\/([A-Za-z0-9]{6})\/?$/.exec(path);
  if (room?.[1]) {
    const code = room[1].toUpperCase();
    return <RoomPage key={code} code={code} />;
  }
  return <NotFound />;
}

export function App() {
  const path = usePath();
  const consent = useConsent();
  // Nach dem Zustimmen wird die aktuelle Seite nachgemeldet; ohne Zustimmung passiert nichts.
  useEffect(() => trackPageView(path), [path, consent]);
  if (path === "/admin" || path.startsWith("/admin/")) {
    return (
      <Suspense fallback={null}>
        <Admin path={path} />
      </Suspense>
    );
  }
  return (
    <>
      <Content path={path} />
      <ConsentBanner />
    </>
  );
}
